import { useState } from "react";
import { format, addDays, isWeekend, isBefore, startOfDay } from "date-fns";
import type { RankedSpace } from "../../ai-mode/types";
import { formatAddressShort } from "../utils";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { currentDevice } from "@/features/vision-360/ai-mode/utils/proposalSharedContext";
import type { RoomTrackingContext } from "@/features/vision-360/ai-mode/utils/roomTrackingContext";
import { reportError } from "@/utils/observability";
import { visionService } from "@/services/vision-service";
import type { TicketRequestPayload } from "@/services/vision-service";
import { AVAILABLE_HOURS } from "@/features/vision-360/ticket/constants";
import { useProposalT } from "../i18n/ProposalI18nContext";

const BEATRIZ_SLACK_USER_ID = "U08V0LKTQUS";

function getAvailableDates(validityMs: number, createdAtMs?: number): Date[] {
  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);
  let validity = startOfDay(new Date(validityMs));

  if (isBefore(validity, tomorrow)) {
    const fromCreatedAt = createdAtMs
      ? addDays(startOfDay(new Date(createdAtMs)), 7)
      : null;
    validity =
      fromCreatedAt && !isBefore(fromCreatedAt, tomorrow)
        ? fromCreatedAt
        : addDays(today, 7);
  }

  const dates: Date[] = [];
  let cursor = tomorrow;
  while (!isBefore(validity, cursor)) {
    if (!isWeekend(cursor)) {
      dates.push(new Date(cursor));
    }
    cursor = addDays(cursor, 1);
  }
  return dates;
}

interface ScheduleSelection {
  date: Date | null;
  time: string | null;
}

interface ScheduleModalProps {
  proposalId: string;
  dealId?: string | null;
  clientSegment?: string;
  /** Ranking context per room, built by the page from the FULL ranked list
   * (this modal only receives the visit-selected subset). */
  roomContextFor?: (spaceId: string) => RoomTrackingContext;
  isOpen: boolean;
  spaces: RankedSpace[];
  validityMs: number;
  createdAt?: string;
  visitorEmail?: string;
  proposalOrigin?: string;
  onScheduled?: (spaceIds: string[]) => void;
  onClose: () => void;
}

function trackVisitsScheduled(
  proposalId: string,
  dealId: string | null | undefined,
  clientSegment: string | undefined,
  roomContextFor: ((spaceId: string) => RoomTrackingContext) | undefined,
  spaces: RankedSpace[],
  selections: Record<string, ScheduleSelection>,
  confirmedIds: string[],
) {
  sendTrackDataServer({
    eventName: "VISION_PROPOSAL_VISITS_SCHEDULED",
    productType: "PO",
    url: window.location.pathname,
    data: {
      proposal_uuid: proposalId,
      deal_id: dealId ?? null,
      device: currentDevice(),
      client_segment: clientSegment || null,
      room_ids: confirmedIds,
      visits: confirmedIds.map((spaceId) => {
        const space = spaces.find((s) => s.id === spaceId);
        const sel = selections[spaceId];
        return {
          room_id: spaceId,
          room_name: space?.roomName ?? "",
          room_address: space?.address ?? "",
          date: sel?.date ? format(sel.date, "yyyy-MM-dd") : "",
          time: sel?.time ?? "",
          // Ranking context at the point of conversion — "do top-ranked
          // rooms convert?" reads straight off this event, no join needed.
          ...roomContextFor?.(spaceId),
        };
      }),
      occurred_at: new Date().toISOString(),
    },
  }).catch(() => {
    // ignore tracking errors
  });
}

export function ScheduleModal({
  proposalId,
  dealId,
  clientSegment,
  roomContextFor,
  isOpen,
  spaces,
  validityMs,
  createdAt,
  visitorEmail = "",
  proposalOrigin,
  onScheduled,
  onClose,
}: ScheduleModalProps) {
  const [selections, setSelections] = useState<
    Record<string, ScheduleSelection>
  >({});
  const [confirmedSpaceIds, setConfirmedSpaceIds] = useState<Set<string>>(
    new Set(),
  );
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const { t } = useProposalT();

  if (!isOpen) return null;

  const availableDates = getAvailableDates(
    validityMs,
    createdAt ? new Date(createdAt).getTime() : undefined,
  );
  const formattedValidity = format(new Date(validityMs), "dd/MM/yyyy");

  const handleSelectDate = (space: RankedSpace, date: Date) => {
    setSelections((prev) => ({
      ...prev,
      [space.id]: { date, time: prev[space.id]?.time ?? null },
    }));
  };

  const setTime = (spaceId: string, time: string) => {
    setSelections((prev) => ({
      ...prev,
      [spaceId]: { date: prev[spaceId]?.date ?? null, time },
    }));
  };

  const pendingSpaces = spaces.filter((s) => !confirmedSpaceIds.has(s.id));

  const allSelected = pendingSpaces.every((s) => {
    const sel = selections[s.id];
    return sel?.date != null && sel?.time != null;
  });

  const handleConfirm = async () => {
    if (!allSelected) return;
    setLoading(true);

    const proposalBaseUrl =
      import.meta.env.VITE_VISION_PROPOSAL_URL ??
      `${window.location.origin}/vision/proposta`;
    const proposalLink = `${proposalBaseUrl}/${proposalId}`;

    const lines = spaces.map((space, idx) => {
      const sel = selections[space.id]!;
      const dateLabel = format(sel.date!, "dd/MM/yyyy");
      return (
        `${idx + 1}. *${space.roomName}* | Space: ${space.spaceName}\n` +
        `   Room ID: ${space.searchId != null && space.searchId !== 0 ? space.searchId : space.id} | Space ID: ${space.partnerId ?? "—"}\n` +
        `   📅 ${dateLabel} às ${sel.time}`
      );
    });

    const isWandaOrigin = proposalOrigin === "Wanda + Bruna SDR";
    const message =
      `🗓️ Solicitação de agendamento de visitas\n\n` +
      (isWandaOrigin ? `<@${BEATRIZ_SLACK_USER_ID}>\n\n` : "") +
      `👤 E-mail: ${visitorEmail}\n` +
      `🔗 Proposta: ${proposalLink}\n\n` +
      `📋 Salas:\n${lines.join("\n\n")}\n\n` +
      `🎫 Ticket aberto no HubSpot`;

    visionService.notifySlack("visit_scheduled", message);

    const ticketPayload: TicketRequestPayload = {
      deal_id: dealId ?? null,
      visit_requester_email: visitorEmail || null,
      proposal_uuid: proposalId,
      reason: "agendamento_visitas",
      rooms: spaces.map((space) => ({
        private_room_id: space.id,
        search_id: space.searchId ?? null,
        space_id: space.partnerId ?? space.id,
        room_name: space.roomName,
        space_name: space.spaceName,
        address: space.address,
        requested_visit_date: format(selections[space.id]!.date!, "yyyy-MM-dd"),
        requested_visit_time: selections[space.id]!.time!,
      })),
      deal_name: null,
      company_name: null,
      office_type: null,
      city: "",
      neighborhoods: [],
      size_m2: null,
      positions: null,
      budget: "",
      expected_entry_date: "",
      requires_documentation: false,
      required_documents: [],
      required_documents_other: null,
      observations: "",
      search_more_options: null,
      contract_months: null,
      open_to_nearby_neighborhoods: null,
      private_meeting_rooms: null,
      meeting_room_capacities: [],
      willing_to_adapt_amenities: null,
      specific_partner_or_restrictions: "",
    };
    visionService.createTicketRequest(ticketPayload).catch((err) => {
      reportError(err, { operation: "createTicketRequest", proposalId });
    });

    const confirmedIds = spaces.map((s) => s.id);
    setConfirmedSpaceIds(new Set(confirmedIds));
    trackVisitsScheduled(
      proposalId,
      dealId,
      clientSegment,
      roomContextFor,
      spaces,
      selections,
      confirmedIds,
    );
    onScheduled?.(confirmedIds);

    setLoading(false);
    setConfirmed(true);
  };

  const handleClose = () => {
    setSelections({});
    setConfirmedSpaceIds(new Set());
    setConfirmed(false);
    setLoading(false);
    onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "92%",
          maxWidth: "600px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
          position: "relative",
          fontFamily: "'Space Grotesk', sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {confirmed ? (
          <SuccessScreen
            spaces={spaces}
            selections={selections}
            onClose={handleClose}
          />
        ) : (
          <>
            {/* Header */}
            <div
              style={{
                padding: "24px 28px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "#1A1814",
                    margin: "0 0 4px",
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  {t("schedule.title")}
                </h2>
                <p style={{ fontSize: "13px", color: "#78736D", margin: 0 }}>
                  {t("schedule.subtitle")}
                </p>
              </div>
              <button
                onClick={handleClose}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  border: "1px solid #E8E6E3",
                  background: "white",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#78736D"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Validity notice */}
            <div
              style={{
                margin: "16px 28px 0",
                padding: "10px 14px",
                borderRadius: "10px",
                background: "#FFFBEB",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#B8860B"
                strokeWidth="2"
                style={{ flexShrink: 0 }}
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              <span style={{ fontSize: "12px", color: "#92400E" }}>
                {t("schedule.availableUntil")}{" "}
                <strong>{formattedValidity}</strong>
              </span>
            </div>

            {/* Content */}
            <div
              style={{
                padding: "16px 28px 24px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {/* Per-space cards */}
              {spaces.map((space, idx) => (
                <SpaceScheduleCard
                  key={space.id}
                  index={idx}
                  space={space}
                  selection={selections[space.id] ?? { date: null, time: null }}
                  availableDates={availableDates}
                  onSelectDate={(d) => handleSelectDate(space, d)}
                  onSelectTime={(t) => setTime(space.id, t)}
                  isConfirmed={confirmedSpaceIds.has(space.id)}
                />
              ))}
            </div>

            {/* Confirm button */}
            <div style={{ padding: "0 28px 24px" }}>
              <button
                onClick={handleConfirm}
                disabled={!allSelected || loading}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: "12px",
                  border: "none",
                  background: allSelected ? "#FFD900" : "#E8E6E3",
                  color: allSelected ? "#1A1814" : "#9E9A94",
                  fontSize: "14px",
                  fontWeight: 700,
                  cursor: allSelected && !loading ? "pointer" : "not-allowed",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                {loading ? (
                  <>
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "50%",
                        border: "2px solid #78736D",
                        borderTopColor: "transparent",
                        animation: "spin 0.7s linear infinite",
                        display: "inline-block",
                      }}
                    />
                    {t("schedule.sending")}
                  </>
                ) : (
                  t("schedule.confirm")
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SpaceScheduleCard({
  index,
  space,
  selection,
  availableDates,
  onSelectDate,
  onSelectTime,
  isConfirmed,
}: {
  index: number;
  space: RankedSpace;
  selection: ScheduleSelection;
  availableDates: Date[];
  onSelectDate: (d: Date) => void;
  onSelectTime: (t: string) => void;
  isConfirmed?: boolean;
}) {
  const { t, getDateLocale } = useProposalT();
  const hasSelection = selection.date != null && selection.time != null;

  return (
    <div
      style={{
        border: isConfirmed
          ? "1px solid #BBF7D0"
          : hasSelection
            ? "1px solid #FFD900"
            : "1px solid #E8E6E3",
        borderRadius: "16px",
        background: isConfirmed
          ? "#F0FDF4"
          : hasSelection
            ? "#FFFDF5"
            : "#FAFAF8",
        padding: "20px",
        transition: "all 0.2s",
      }}
    >
      {/* Space header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "10px",
            background: "#1A1814",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "13px",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {index + 1}
        </div>
        <div style={{ flex: 1 }}>
          <p
            style={{
              fontSize: "15px",
              fontWeight: 600,
              color: "#1A1814",
              margin: 0,
            }}
          >
            {space.roomName}
          </p>
          {space.address && (
            <p
              style={{ fontSize: "12px", color: "#78736D", margin: "2px 0 0" }}
            >
              {formatAddressShort(space.address)}
            </p>
          )}
        </div>
        {hasSelection && (
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: "#ECFDF5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#22C55E"
              strokeWidth="2.5"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
        )}
      </div>

      {/* Confirmed state */}
      {isConfirmed && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 14px",
            borderRadius: "10px",
            background: "#DCFCE7",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#16A34A"
            strokeWidth="2.5"
            style={{ flexShrink: 0 }}
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
          <span style={{ fontSize: "13px", color: "#15803D", fontWeight: 600 }}>
            {t("schedule.sentLabel")}{" "}
            {selection.date
              ? format(selection.date, "EEE dd/MM", { locale: getDateLocale() })
              : ""}{" "}
            {t("schedule.at")} {selection.time}
          </span>
        </div>
      )}

      {/* Date/time pickers — hidden when already confirmed */}
      {!isConfirmed && (
        <>
          <p
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#78736D",
              margin: "0 0 8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {t("schedule.chooseDate")}
          </p>

          {/* Date chips */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              marginBottom: "16px",
            }}
          >
            {availableDates.slice(0, 10).map((date) => {
              const isSelected =
                selection.date?.toDateString() === date.toDateString();
              return (
                <button
                  key={date.toISOString()}
                  onClick={() => onSelectDate(date)}
                  style={{
                    padding: "7px 12px",
                    borderRadius: "10px",
                    border: isSelected
                      ? "1.5px solid #FFD900"
                      : "1px solid #E8E6E3",
                    background: isSelected ? "#FFD900" : "white",
                    color: "#1A1814",
                    fontSize: "13px",
                    fontWeight: isSelected ? 600 : 500,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {format(date, "EEE dd/MM", { locale: getDateLocale() })}
                </button>
              );
            })}
          </div>

          {/* Time slots — only shown after date is selected */}
          {selection.date && (
            <>
              <p
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#78736D",
                  margin: "0 0 8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {t("schedule.chooseTime")}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {AVAILABLE_HOURS.map((hour) => {
                  const isSelected = selection.time === hour;
                  return (
                    <button
                      key={hour}
                      onClick={() => onSelectTime(hour)}
                      style={{
                        padding: "7px 12px",
                        borderRadius: "10px",
                        border: isSelected
                          ? "1.5px solid #FFD900"
                          : "1px solid #E8E6E3",
                        background: isSelected ? "#FFD900" : "white",
                        color: "#1A1814",
                        fontSize: "13px",
                        fontWeight: isSelected ? 600 : 500,
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      {hour}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Selection summary */}
          {hasSelection && (
            <div
              style={{
                marginTop: "12px",
                padding: "10px 14px",
                borderRadius: "10px",
                background: "#F0FDF4",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#16A34A"
                strokeWidth="2"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span
                style={{ fontSize: "13px", color: "#15803D", fontWeight: 500 }}
              >
                {format(selection.date!, "EEE dd/MM", {
                  locale: getDateLocale(),
                })}{" "}
                {t("schedule.at")} {selection.time}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SuccessScreen({
  spaces,
  selections,
  onClose,
}: {
  spaces: RankedSpace[];
  selections: Record<string, ScheduleSelection>;
  onClose: () => void;
}) {
  const { t, getDateLocale } = useProposalT();
  const visits = spaces
    .filter((s) => selections[s.id]?.date && selections[s.id]?.time)
    .sort((a, b) => {
      const da = selections[a.id]?.date?.getTime() ?? 0;
      const db = selections[b.id]?.date?.getTime() ?? 0;
      return da - db;
    });

  return (
    <div style={{ padding: "40px 32px", textAlign: "center" }}>
      <div
        style={{
          width: "64px",
          height: "64px",
          borderRadius: "50%",
          background: "#ECFDF5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#22C55E"
          strokeWidth="2.5"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>
      <h3
        style={{
          fontSize: "22px",
          fontWeight: 700,
          color: "#1A1814",
          margin: "0 0 6px",
          fontFamily: "'Space Grotesk', sans-serif",
        }}
      >
        {t("schedule.successTitle")}
      </h3>
      <p style={{ fontSize: "14px", color: "#78736D", margin: "0 0 24px" }}>
        {t("schedule.successSubtitle")}
      </p>

      <div style={{ textAlign: "left", marginBottom: "18px" }}>
        {visits.map((space) => {
          const sel = selections[space.id]!;
          return (
            <div
              key={space.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 16px",
                background: "#F5F3EF",
                borderRadius: "10px",
                marginBottom: "8px",
              }}
            >
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  background: "#FFD900",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1A1814"
                  strokeWidth="2"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div style={{ flex: 1, textAlign: "left" }}>
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#1A1814",
                    margin: 0,
                  }}
                >
                  {space.roomName}
                </p>
                {space.address && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#78736D",
                      margin: "2px 0 0",
                    }}
                  >
                    {formatAddressShort(space.address)}
                  </p>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <p
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#1A1814",
                    margin: 0,
                  }}
                >
                  {format(sel.date!, "EEE dd/MM", { locale: getDateLocale() })}
                </p>
                <p
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: "#1A1814",
                    margin: 0,
                  }}
                >
                  {sel.time}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <p
        style={{
          fontSize: "13px",
          color: "#78736D",
          margin: "0 0 20px",
          padding: "12px 16px",
          background: "#FFFBEB",
          borderRadius: "10px",
          textAlign: "left",
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#B8860B"
          strokeWidth="2"
          style={{
            display: "inline",
            verticalAlign: "-2px",
            marginRight: "6px",
          }}
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        {t("schedule.successNotice")}
      </p>

      <button
        onClick={onClose}
        style={{
          width: "100%",
          padding: "14px",
          borderRadius: "12px",
          background: "#1A1814",
          color: "white",
          fontSize: "14px",
          fontWeight: 700,
          border: "none",
          cursor: "pointer",
          fontFamily: "'Space Grotesk', sans-serif",
        }}
      >
        {t("schedule.understood")}
      </button>
    </div>
  );
}
